import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { type FetchMeterHistoryValuesPayload, type UpdateMeterRequest } from './meter-types'
import selectIdToken from '../selectors/select-id-token'
import { selectFromState } from '../selectors/select-from-state'
import {
  DeviceApi,
  type DeviceDeleteResponse,
  type DeviceHistoryResponse,
  type DeviceUpdateResponse,
  type FetchMeterHistoryQueryParams,
  type GetDevicesResponse,
  type UpdateMeterRequestPayload
} from '../../api/device-api'
import { ApiUtil } from '../../api/api-utils'
import { LogoutSuccessAction } from '../auth/auth-actions'
import {
  DeleteMeterAction,
  DeleteMeterSuccessAction,
  FetchMeterHistoryValuesAction,
  FetchMeterHistoryValuesFailedAction,
  FetchMeterHistoryValuesSuccessAction,
  FetchMetersAction,
  FetchMetersFailureAction,
  FetchMetersSuccessAction,
  UpdateMeterAction,
  UpdateMeterSuccessAction
} from './meter-actions'
import { AddMessageAction } from '../messages/messages-actions'
import { MessageTypes } from '../messages/messages-types'

/* Sage Generator Functions */

// Note: disabling requiring explicit return types for saga generators
// (as the type is pretty complex and produces little value as explicit)

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * deleteMeter(action: { payload: { meterId: string } }) {
  const deviceId: string = action.payload.meterId

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: DeviceDeleteResponse = yield call(DeviceApi.deleteDevice, idToken, deviceId)
  if (response.succeeded) {
    yield all([
      put(DeleteMeterSuccessAction({ meterId: deviceId })),
      put(FetchMetersAction())
    ])
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield put(AddMessageAction(response.errorKey ?? 'device.deleteDeviceDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * fetchMeters() {
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: GetDevicesResponse = yield call(DeviceApi.getDevices, idToken)

  if (response.succeeded) {
    yield put(FetchMetersSuccessAction({ meters: response.payload.devices }))
  } else {
    console.warn('Error received on fetching all devices:', ApiUtil.failedResponseToDisplayString(response))
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield all([
      put(FetchMetersFailureAction()),
      put(AddMessageAction(response.errorKey ?? 'device.fetchDevicesDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * fetchMeterHistoryValues(action: { payload: FetchMeterHistoryValuesPayload }) {
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const queryParams: FetchMeterHistoryQueryParams = {
    start: action.payload.start,
    end: action.payload.end
  }

  const response: DeviceHistoryResponse =
        yield call(DeviceApi.getDeviceHistoryValues, idToken, action.payload.id, queryParams)

  if (response.succeeded) {
    yield put(FetchMeterHistoryValuesSuccessAction({ data: response.payload.items, scale: action.payload.scale }))
  } else {
    console.warn('Error received on device history data:', ApiUtil.failedResponseToDisplayString(response))
    yield put(FetchMeterHistoryValuesFailedAction())
    yield put(AddMessageAction(response.errorKey ?? 'device.fetchHistoryDataDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * updateMeter(action: { payload: UpdateMeterRequest }) {
  const { payload } = action
  const deviceId = payload.meterId
  const reqPayload: UpdateMeterRequestPayload = {
    alarmSettings: {
      ...payload.alarmSettings
    },
    baseValue: payload.baseValue,
    labels: payload.labels,
    location: payload.location,
    pulseInLiters: payload.pulseInLiters,
    serialNumber: payload.serialNumber,
    transmitterSerialNumber: payload.transmitterSerialNumber
  }

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: DeviceUpdateResponse = yield call(DeviceApi.updateDevice, idToken, deviceId, reqPayload)

  if (response.succeeded) {
    yield put(UpdateMeterSuccessAction(response.payload.device))
  } else {
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }

    yield put(AddMessageAction(response.errorKey ?? 'device.updateDeviceDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function * meterSagas() {
  yield takeEvery(DeleteMeterAction, deleteMeter)
  yield takeLatest(FetchMetersAction, fetchMeters)
  yield takeLatest(FetchMeterHistoryValuesAction, fetchMeterHistoryValues)
  yield takeEvery(UpdateMeterAction, updateMeter)
}
